/**
 * brain-select — the index is the corpus (Nami Phase 4 Train 2, spec §2).
 *
 * Selection never reads a page body. Its corpus is one line per thing the agent
 * could open: the compiled knowledge map (`knowledge/index.md`, one line per
 * page), the evidence map (`evidence/index.md`, one line per proof card) and one
 * synthesized line per note. The same ranker serves the box (over the volume's
 * files) and the server (over the published head), so the composer and the task
 * pick the same pages for the same question.
 *
 * The six self pages are never traded against the budget: rendered first, whole,
 * in contract order, unranked (the founder's law: the user's own pages rank
 * first whenever retrieval trades against anything else).
 *
 * Lexical on purpose (tf-idf over the line, the family `recallFromStore` uses):
 * the index line is the compile's own summary of the page, written for exactly
 * this; a smarter scorer is a swap behind `rankIndexLines` once the paired eval
 * says lexical is what misses (Law 7).
 */
import { MEMORY_ROOT, TREE, evidenceStubPath, parseCiteId } from './memory-tree.js';
import { SELF_PAGES, isSelfPagePath, selfPagePath } from './self-model.js';

export type IndexTier = 'knowledge' | 'notes' | 'evidence';

export interface IndexLine {
  tier: IndexTier;
  /** Workspace-relative, starts with `memory/` — what the agent hands to Read. */
  path: string;
  title: string;
  /** The rest of the line: facts, the note's first prose line, the card's first text line. */
  text: string;
  updated?: string;
  cite?: string;
  /** Evidence only: whether the stub is on the volume (unknown = undefined). */
  onDisk?: boolean;
}

export const K_PAGES = 8;
export const K_PROOFS = 4;
export const TREE_SELECT_MAX_CHARS = 2_400;
export const TREE_SELECT_MAX_LINES = 20_000;
export const MAP_HEAD_LINES = 24;
export const FETCH_PROOF_MAX = 4;

/** `- [[stem|Title]] — facts · updated date`, the compile's line (nami-brain-index.service.ts). */
const KNOWLEDGE_LINE_RE = /^- \[\[([^\]|]+)(?:\|([^\]]*))?\]\](?: — (.*))?$/;
/** `- YYYY-MM-DD \`cite\` Title`, the evidence map's line (renderEvidenceIndex). */
const EVIDENCE_LINE_RE = /^- (\d{4}-\d{2}-\d{2}) `([^`]+)` (.*)$/;
/**
 * The compile writes `updated` in two shapes: a bare date, or a full ISO
 * stamp (`2026-09-12T19:00:00.000Z`). Either way only the ten-character date
 * is kept (RULING P4T2-R3) — the index line is a summary, not a timestamp.
 */
const UPDATED_RE = /(?:^| · )updated (\d{4}-\d{2}-\d{2})(?:T\S*)?$/;

export function treeRelative(path: string): string {
  return path.startsWith(`${MEMORY_ROOT}/`) ? path.slice(MEMORY_ROOT.length + 1) : path;
}

export function parseKnowledgeIndex(md: string): IndexLine[] {
  const out: IndexLine[] = [];
  for (const raw of md.split('\n')) {
    const m = KNOWLEDGE_LINE_RE.exec(raw.trim());
    if (!m) continue;
    const stem = m[1]!.trim();
    const title = (m[2] ?? '').trim() || stem.split('/').pop() || stem;
    let text = (m[3] ?? '').trim();
    let updated: string | undefined;
    const u = UPDATED_RE.exec(text);
    if (u) {
      updated = u[1];
      text = text.slice(0, u.index).trim();
    }
    const line: IndexLine = { tier: 'knowledge', path: `${TREE.knowledge.root}/${stem}.md`, title, text };
    if (updated) line.updated = updated;
    out.push(line);
  }
  return out;
}

export function parseEvidenceIndex(md: string): IndexLine[] {
  const out: IndexLine[] = [];
  for (const raw of md.split('\n')) {
    const m = EVIDENCE_LINE_RE.exec(raw.trim());
    if (!m) continue;
    const cite = m[2]!;
    const parsed = parseCiteId(cite);
    if (!parsed) continue;
    const rel = parsed.door === 'sense' ? evidenceStubPath('sense', parsed.id, parsed.app) : evidenceStubPath(parsed.door, parsed.id);
    out.push({ tier: 'evidence', path: rel, title: m[3]!.trim(), text: '', updated: m[1]!, cite });
  }
  return out;
}

export function notesIndexLine(path: string, firstHeading: string | null, firstProse: string | null): IndexLine {
  const file = path.split('/').pop() ?? path;
  const title = (firstHeading ?? '').trim() || file.replace(/\.md$/, '');
  const fullPath = path.startsWith(`${MEMORY_ROOT}/`) ? path : `${MEMORY_ROOT}/${path}`;
  return { tier: 'notes', path: fullPath, title, text: (firstProse ?? '').trim() };
}

const STOPWORDS = [
  'the', 'a', 'an', 'of', 'to', 'in', 'on', 'for', 'and', 'or', 'is', 'are', 'was', 'be', 'been',
  'it', 'its', 'this', 'that', 'these', 'those', 'my', 'me', 'mine', 'you', 'your', 'yours',
  'he', 'him', 'his', 'she', 'her', 'hers', 'they', 'them', 'their', 'we', 'us', 'our',
  'what', 'which', 'who', 'whom', 'how', 'when', 'where', 'why', 'do', 'does', 'did',
  'with', 'at', 'about', 'from', 'as', 'by', 'not', 'no', 'so', 'if', 'then', 'than',
  'can', 'could', 'should', 'would', 'will', 'just', 'have', 'has', 'had', 'tools',
];
const STOPWORD_RE = new RegExp(`^(?:${STOPWORDS.join('|')})$`);
/**
 * RULING P4T2-R7: a word longer than 3 chars ending in `s` always yields its
 * single-`s`-stripped stem; a word longer than 4 chars ending in `es` ALSO
 * yields the `es`-stripped stem as a second term. Both the query and the
 * corpus run through the same `terms()`, so `"launches"` → `["launche",
 * "launch"]` meets the corpus term `"launch"`, while `"sizes"` → `["size",
 * "siz"]` and `"caches"` → `["cache", "cach"]` still meet `"size"`/`"cache"`.
 */
const fold = (w: string): string[] => {
  if (w.length > 3 && w.endsWith('s')) {
    const out = [w.slice(0, -1)];
    if (w.length > 4 && w.endsWith('es')) out.push(w.slice(0, -2));
    return out;
  }
  return [w];
};

function terms(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 1 && !STOPWORD_RE.test(w))
    .flatMap(fold);
}

export function hasSelectTerms(query: string): boolean {
  return terms(query).length > 0;
}

/** The engine's consult query is `<user text>\n[tools: A, B]`; the tree arm ranks the words. */
export function stripToolNames(query: string): string {
  const at = query.indexOf('\n[tools:');
  return at >= 0 ? query.slice(0, at).trim() : query.trim();
}

const RECENT_DAYS = 14;
const RECENT_BOOST = 1.15;
const TITLE_BOOST = 1.5;

function lineTerms(l: IndexLine): string[] {
  return terms(`${l.title} ${l.text} ${treeRelative(l.path).replace(/\.md$/, '').split('/').join(' ')}`);
}

/** Term → occurrence count within one line, computed once per line rather than re-scanned per query term. */
function termCounts(ts: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const t of ts) counts.set(t, (counts.get(t) ?? 0) + 1);
  return counts;
}

export function rankIndexLines(lines: IndexLine[], query: string, k: number, now: Date = new Date()): IndexLine[] {
  const q = [...new Set(terms(query))];
  if (q.length === 0 || k <= 0) return [];
  const lts = lines.map(lineTerms);
  const counts = lts.map(termCounts);
  const n = lines.length;
  const df = new Map<string, number>();
  for (const t of q) df.set(t, counts.filter((c) => c.has(t)).length);
  const qPhrase = query.toLowerCase().trim();
  const cutoff = now.getTime() - RECENT_DAYS * 86_400_000;
  return lines
    .map((l, i) => {
      const ts = lts[i]!;
      const c = counts[i]!;
      let score = 0;
      for (const t of q) {
        const tf = c.get(t) ?? 0;
        if (tf > 0) score += tf * Math.log(1 + n / (1 + (df.get(t) ?? 0)));
      }
      if (score === 0) return { l, score };
      score /= Math.sqrt(ts.length || 1);
      if (qPhrase && l.title.toLowerCase().includes(qPhrase)) score *= TITLE_BOOST;
      if (l.updated && Date.parse(l.updated) >= cutoff) score *= RECENT_BOOST;
      return { l, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, k)
    .map((s) => s.l);
}

export interface BrainSelection {
  head: string | null;
  counts: { pages: number; notes: number; proofs: number };
  six: IndexLine[];
  pages: IndexLine[];
  proofs: IndexLine[];
}

export function selectFromLines(lines: IndexLine[], query: string, head: string | null, now: Date = new Date()): BrainSelection {
  const byPath = new Map(lines.map((l) => [l.path, l] as const));
  const six: IndexLine[] = [];
  for (const p of SELF_PAGES) {
    const hit = byPath.get(`${MEMORY_ROOT}/${selfPagePath(p.slug)}`);
    if (hit) six.push(hit);
  }
  const knowledge = lines.filter((l) => l.tier === 'knowledge');
  const notes = lines.filter((l) => l.tier === 'notes');
  const evidence = lines.filter((l) => l.tier === 'evidence');
  const counts = { pages: knowledge.length, notes: notes.length, proofs: evidence.length };
  if (!hasSelectTerms(query)) return { head, counts, six, pages: [], proofs: [] };
  const pageCorpus = [...knowledge.filter((l) => !isSelfPagePath(treeRelative(l.path))), ...notes];
  return {
    head,
    counts,
    six,
    pages: rankIndexLines(pageCorpus, query, K_PAGES, now),
    proofs: rankIndexLines(evidence, query, K_PROOFS, now),
  };
}

/** RULING P4T2-R6: every field in the rendered block joins on ` · ` — no em dash anywhere. */
export function lineText(l: IndexLine): string {
  if (l.tier === 'evidence') return `- ${l.path} · ${l.updated ?? ''} · ${l.title}${l.onDisk === false ? ` (not on disk; cite ${l.cite})` : ''}`;
  const parts = [l.title, l.text, ...(l.updated ? [`updated ${l.updated}`] : [])].filter(Boolean);
  return `- ${l.path} · ${parts.join(' · ')}`;
}

function headLine(sel: BrainSelection, lead: string): string {
  const head = sel.head ? `head ${sel.head}` : 'head unknown';
  return `${lead} (${head} · ${sel.counts.pages} pages · ${sel.counts.notes} notes · ${sel.counts.proofs} proof cards)`;
}

/** One rendered row: a section label (no `line`) or an index line that carries one. */
interface RenderedRow {
  text: string;
  line?: IndexLine;
}
const label = (text: string): RenderedRow => ({ text });
const row = (line: IndexLine): RenderedRow => ({ text: lineText(line), line });

/**
 * Whole lines only, under the cap; the six are never dropped. Returns the kept
 * index lines beside the text, because the cap decides what the model was SHOWN
 * and any consumer reporting the pick must read that, not the selection.
 */
function joinUnderCap(parts: RenderedRow[], cap: number): { text: string; kept: IndexLine[] } {
  let text = '';
  const kept: IndexLine[] = [];
  for (const p of parts) {
    const next = text ? `${text}\n${p.text}` : p.text;
    if (next.length > cap) break;
    text = next;
    if (p.line) kept.push(p.line);
  }
  return { text, kept };
}

/**
 * RULING P4T2-R10 — the rendered block AND the lines it actually kept, in the order
 * it printed them. Two things make the selection a lie about what the model saw: the
 * cap drops trailing lines, and `sel.pages` is rank-ordered while the block prints
 * knowledge pages before notes. Every consumer that REPORTS the pick (the consult's
 * `agent:memory_recall` announcement) reads `lines`; a consumer that only needs the
 * bytes calls `renderBrainSelection` below.
 */
export function renderBrainSelectionWithLines(sel: BrainSelection): { text: string; lines: IndexLine[] } {
  const fixed = [`${headLine(sel, 'From your brain')}, picked for this task.`, 'Your six pages, always first:', ...sel.six.map(lineText)];
  const rest: RenderedRow[] = [];
  const notes = sel.pages.filter((l) => l.tier === 'notes');
  const pages = sel.pages.filter((l) => l.tier === 'knowledge');
  if (pages.length) rest.push(label('Pages that may matter here:'), ...pages.map(row));
  if (notes.length) rest.push(label('Notes that may matter here:'), ...notes.map(row));
  if (sel.proofs.length) rest.push(label('Proof that may matter here:'), ...sel.proofs.map(row));
  const fixedText = fixed.join('\n');
  const { text: restText, kept } = joinUnderCap(rest, TREE_SELECT_MAX_CHARS - fixedText.length - 1);
  return {
    text: restText ? `${fixedText}\n${restText}` : fixedText,
    lines: [...sel.six, ...kept],
  };
}

export function renderBrainSelection(sel: BrainSelection): string {
  return renderBrainSelectionWithLines(sel).text;
}

/** The empty-query answer: the six, the counts, the map's first lines. Never a body. */
export function renderBrainMap(sel: BrainSelection, indexLines: IndexLine[]): string {
  const sixPaths = new Set(sel.six.map((l) => l.path));
  const head = indexLines.filter((l) => l.tier === 'knowledge' && !sixPaths.has(l.path)).slice(0, MAP_HEAD_LINES);
  const footer = `The whole map is ${TREE.knowledge.index}.`;
  const parts: RenderedRow[] = [
    label(`${headLine(sel, 'Your brain')}.`),
    label('Your six pages:'),
    ...sel.six.map(row),
    label('The map begins:'),
    ...head.map(row),
  ];
  const { text: body } = joinUnderCap(parts, TREE_SELECT_MAX_CHARS - footer.length - 1);
  return `${body}\n${footer}`;
}
